/*
 * File: /src/services/agentService.js
 * Created Date: Thursday May 6th 2021
 * Author: Nicolas Wegener, Roy Collins
 * -----
 * Last Modified: Thursday, 6th May 2021 9:21:48 am
 * Modified By: Nicolas Wegener (wegenern@gmail.com)
 * -----
 * Copyright (c) 2021 AdBase, Inc.
 */

import { authedAxios } from '../shared/axiosWrapper';
import { AD_BASE_AGENTS_PATH, AD_BASE_USER_PATH } from '../constants';

export default {
  getAgents(after_id, limit) {
    // This endpoint does not support pagination yet
    const params = {
      after_id: after_id ? after_id : 0,
      limit: limit ? limit : 100,
      sort: 'first_name:asc',
    };
    return authedAxios.get(AD_BASE_USER_PATH + 'agents/').then(r => r.data);
  },
  // Get all logged in agents, not just active state
  getActiveAgents() {
    // This endpoint does not support pagination yet
    /*
    const params = {
      after_id: afterId ? afterId : 0,
      limit: limit ? limit : 100
    };
    */
    return authedAxios.get(AD_BASE_AGENTS_PATH).then(r => r.data);
  },
  login() {
    return authedAxios.post(AD_BASE_AGENTS_PATH + 'login/').then(
      // Will return the access token
      r => r.data
    );
  },
  renewKey() {
    return authedAxios.post(AD_BASE_AGENTS_PATH + 'renew-key/').then(
      // Will return the access token
      r => r.data
    );
  },
  logout() {
    return authedAxios.post(AD_BASE_AGENTS_PATH + 'logout/').then(
      // Will return the access token
      r => r.data
    );
  },
  forceLogout(userId) {
    return authedAxios.post(
      AD_BASE_AGENTS_PATH + 'force-logout/',
      { user_id: userId },
    )
      .then( r => r.data );
  },
  getConfig() {
    return authedAxios.get(AD_BASE_AGENTS_PATH + 'status-config/').then(
      // Will return the access token
      r => r.data
    );
  },
  getStatus() {
    return authedAxios.get(AD_BASE_AGENTS_PATH + 'status/').then(r => r.data);
  },
  setStatus(newStatus) {
    return authedAxios
      .post(AD_BASE_AGENTS_PATH + 'status/', { new_status: newStatus })
      .then(
        // Will return the access token
        r => r.data
      );
  },
  setDestination(destId) {
    return authedAxios
      .put(AD_BASE_AGENTS_PATH + 'destination/', { dial_destination_id: destId })
      .then(
        // Will return the access token
        r => r.data
      );
  },
  getQueueSelect() {
    return authedAxios
      .get(AD_BASE_AGENTS_PATH + 'queue-select/')
      .then(
        // Will return the access token
        r => r.data
      );
  },
  setQueueSelect(userQueues) {
    return authedAxios
      .put(AD_BASE_AGENTS_PATH + 'queue-select/', userQueues)
      .then(
        // Will return the access token
        r => r.data
      );
  },
  answerCall(callId, segmentSid) {
    return authedAxios
      .post(AD_BASE_AGENTS_PATH + 'answer-call/', {
        call_id: callId,
        segment_sid: segmentSid,
      })
      .then(
        // Will return nothing currently, fire and forget
        r => r.data
      );
  },
  rejectCall(callId, segmentSid) {
    return authedAxios
      .post(AD_BASE_AGENTS_PATH + 'reject-call/', {
        call_id: callId,
        segment_sid: segmentSid,
      })
      .then(
        // Will return nothing currently, fire and forget
        r => r.data
      );
  },
  hangUp(callId, segmentId) {
    return authedAxios
      .post(AD_BASE_AGENTS_PATH + 'hang-up/', {
        call_id: callId,
        segment_id: segmentId,
      })
      .then(
        // Will return nothing currently, fire and forget
        r => r.data
      );
  },
  muteSegment(callId, segmentId, mute) {
    return authedAxios
      .post(AD_BASE_AGENTS_PATH + 'mute/', {
        call_id: callId,
        segment_id: segmentId,
        mute: mute,
      })
      .then(
        r => r.data
      );
  },
  holdSegment(callId, segmentId, hold) {
    return authedAxios
      .post(AD_BASE_AGENTS_PATH + 'hold-call/', {
        call_id: callId,
        segment_id: segmentId,
        hold: hold,
      })
      .then(
        r => r.data
      );
  },
  pauseRecording(callId, pause) {
    return authedAxios
      .post(AD_BASE_AGENTS_PATH + 'pause-recording/', {
        call_id: callId,
        pause: pause,
      })
      .then(
        r => r.data
      );
  },
  transferCall(callId, toType, toId, toNumber, warm, userId) {
    return authedAxios
      .post(AD_BASE_AGENTS_PATH + 'transfer-call/', {
        call_id: callId,
        to_type: toType,
        to_id: toId,
        to_number: toNumber, // might be undefined
        warm: warm,
        // Userid is not supported yet,
        // user_id: userId,
      })
      .then(
        // Will return nothing currently, fire and forget
        r => r.data
      );
  },
  // Grab a call from the queue and assign it to this agent
  pickupCall(callId, userId, announce, barge) {
    return authedAxios
      .post(AD_BASE_AGENTS_PATH + 'pickup-call/', {
        call_id: callId,
        user_id: userId,
      })
      .then(
        r => r.data
      );
  },
  listenCall(callId, userId, announce, barge) {
    return authedAxios
      .post(AD_BASE_AGENTS_PATH + 'join-call/', {
        call_id: callId,
        user_id: userId,
        announce: announce,
        barge: barge,
      })
      .then(
        r => r.data
      );
  },
  bargeCall(callId, userId) {
    return authedAxios
      .post(AD_BASE_AGENTS_PATH + 'barge-call/', {
        call_id: callId,
        user_id: userId,
      })
      .then(
        r => r.data
      );
  },
  deleteCall(callId, userId) {
    return authedAxios
      .post(AD_BASE_AGENTS_PATH + 'delete-call/', {
        call_id: callId,
        user_id: userId,
      })
      .then(
        r => r.data
      );
  },
  verbalOptin(callId, userId, contactId, numberId) {
    return authedAxios
      .post(AD_BASE_AGENTS_PATH + 'verbal-optin/', {
        call_id: callId,
        user_id: userId,
        contact_id: contactId,
        number_id: numberId,
      })
      .then(
        r => r.data
      );
  }
};
